import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { VendorPurchasesReport } from "@/api/types";
import "./ReportsVendorPurchases.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** 30 days back from today, UTC — same rolling-window default
 *  `ReportsProductionSales.tsx`'s own `defaultRangeStart` uses, duplicated on purpose
 *  (that file's own reasoning: each report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Vendor purchases summary — Reports' worksheet of bills' face amount, per vendor, for a
 * window the owner chooses. ef-052-3, ADR 0009 §6 ("Reports is one picker" — a report
 * renders inside the template at `/reports/{slug}`, no area tab strip of its own; "All
 * reports" below is the only way back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsProductionSales.tsx`'s own remarks give, restated here for the identical
 * reason: a purchases figure read against a window nobody asked for is a number a farmer
 * did not request. The From/To inputs still carry a sensible rolling-30-day default so
 * the form is never two empty fields.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — unlike `ReportsScheduleF.tsx`/`ReportsPayrollRegister.tsx`, this
 * screen names no `entityId` at all. `VendorPurchasesReportEndpoints` rolls up every bill
 * on the tenant regardless of entity (`VendorPurchasesReportRepository`'s own remarks) —
 * a picker with exactly one option a farmer never needs to touch is the "no fake buttons"
 * call this project already makes elsewhere.
 * </p>
 */
export function ReportsVendorPurchases() {
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [report, setReport] = useState<VendorPurchasesReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getVendorPurchasesReport({ from, to });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the vendor purchases summary."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsProductionSales.tsx`'s identical download gives: paying for the
   * whole CSV body on every date change would be wasted work for the visits that never
   * press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getVendorPurchasesCsv({ from, to });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline ReportsProductionSales.tsx's identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-vendor-purchases-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the vendor purchases file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="vendor-purchases">
      <PageHeader
        title="Vendor purchases summary"
        subtitle="Bills, summed per vendor, over a window you choose"
      />

      <a href="/reports" className="vendor-purchases__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="vendor-purchases__panel"
        aria-labelledby="vendor-purchases-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="vendor-purchases-heading" className="vendor-purchases__heading">
          Summary
        </h2>

        <form className="vendor-purchases__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="vendor-purchases__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="vendor-purchases__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary vendor-purchases__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="vendor-purchases__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        <Why>
          <strong>Bills</strong> only — invoices and voided bills never count. A bill with
          no vendor on it rolls into one line of its own.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the vendor purchases summary."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No bills were found."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="vendor-purchases__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="vendor-purchases-heading"
            >
              <table className="vendor-purchases__table ruled-rows">
                <caption className="vendor-purchases__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Vendor</th>
                    <th scope="col" className="vendor-purchases__num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.counterpartyId ?? "unspecified"}>
                      <th scope="row" className="vendor-purchases__name">{row.name}</th>
                      <td className="vendor-purchases__num vendor-purchases__amount">
                        {formatMoney(row.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="vendor-purchases__export">
              <h3 className="vendor-purchases__export-title">Vendor purchases for your records</h3>
              <p className="vendor-purchases__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary vendor-purchases__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="vendor-purchases__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="vendor-purchases__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
